/**
 * iter1238 Phase 8.11 · StreamingUploadModal
 * ==========================================
 * Unified scrollable modal for a single upload job. Dual-mode:
 *   • active — shows the live parse feed (progress bar, counters,
 *     latest 40 parsed rows) reusing StreamingParsedFeed under the
 *     hood. This is what used to render inline on Bank Feed Center.
 *   • failed — shows the last status_message + error_message and a
 *     Retry button that calls `/api/bank-feeds/upload-jobs/{id}/retry`
 *     to re-fire the streaming finalize.
 *
 * Triggered from the global upload progress banner
 * (`GlobalUploadProgressBadge.jsx`) — the "View progress" and "Review
 * failure" CTAs now open this instead of navigating.
 */
import React, { useState } from 'react';
import { StatementSetProgress } from './StatementSetProgress';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, Loader2, RefreshCcw, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import StreamingParsedFeed from './StreamingParsedFeed';
import { API } from '../../App';

const StreamingUploadModal = ({
  open,
  onOpenChange,
  job,           // { jobId, filename, status, statusMessage, errorMessage, progress }
  onRetryDone,   // called after successful retry dispatch
}) => {
  const [retrying, setRetrying] = useState(false);

  if (!job) return null;
  const isFailed = job.status === 'failed';
  const isActive = ['queued', 'processing', 'resuming', 'needs_mapping', 'learning'].includes(job.status);
  // iter1396 - Structured failure taxonomy from the backend. When the
  // upload job carries a `failure` object, the drawer shows a specific
  // actionable banner (e.g. "This is a legacy .xls file — we already
  // auto-converted and retried") instead of the generic status_message.
  const failure = job.failure || null;
  const suggestion = failure?.suggested_action || null;

  // iter1245 · Layout-reject detection. When the failure copy already
  // tells the user to upload CSV/Excel (or explains it's a scan /
  // password-protected / damaged / unrecognisable layout), retrying
  // the SAME file is guaranteed to fail again. Hide the Retry button
  // in that case — the correct action is to upload a different file.
  const _lcMsg = (job.statusMessage || '').toLowerCase();
  const isLayoutReject =
    (failure?.code === 'legacy_xls_conversion_failed') ||
    (failure?.code === 'password_protected') ||
    (failure?.code === 'unknown_format') ||
    (failure?.code === 'no_header_detected') ||
    (failure?.code === 'encoding_undetectable') ||
    (failure?.code === 'corrupt_zip') ||
    _lcMsg.includes('csv') || _lcMsg.includes('excel') ||
    _lcMsg.includes('scanned') || _lcMsg.includes('layout') ||
    _lcMsg.includes('password') || _lcMsg.includes('damaged');
  // iter1244 · Retry must NEVER be offered for a terminal-blocked
  // failure whose cause is deterministic (continuity mismatch,
  // rejected file, layout reject). Re-firing the pipeline over the
  // same cached rows will hit the exact same block, waste the user's
  // time, and — worst — give the impression that "something is being
  // done" while nothing changes. The correct affordance is to upload
  // a corrected file (a NEW job, not a retry).
  const isTerminalBlocked =
    job.status === 'blocked_continuity' ||
    job.status === 'rejected' ||
    job.status === 'layout_reject';

  const handleRetry = async () => {
    if (!job.jobId) return;
    setRetrying(true);
    try {
      const r = await axios.post(`${API}/bank-feeds/upload-jobs/${job.jobId}/retry`);
      toast.success(r.data?.message || 'Retry started');
      try { onRetryDone && onRetryDone(); } catch (_) { /* noop */ }
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || err?.message
        || 'Could not start retry. Please cancel and re-upload the statement.';
      toast.error(msg);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="streaming-upload-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isFailed ? (
              <AlertCircle className="w-5 h-5 text-rose-500" />
            ) : (
              <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
            )}
            <span data-testid="streaming-upload-modal-title">
              {isFailed ? 'Upload failed' : 'Parse in progress'}
            </span>
          </DialogTitle>
          <DialogDescription
            className="truncate max-w-full"
            title={job.filename}
            data-testid="streaming-upload-modal-filename"
          >
            {job.filename || 'Statement upload'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 -mr-1">
          {isFailed && (
            <div
              className="rounded-lg border border-rose-200 bg-rose-50 p-4 mb-3"
              data-testid="streaming-upload-modal-failure-body"
            >
              <div className="text-sm font-medium text-rose-900 mb-1">
                What happened
              </div>
              <div className="text-sm text-rose-800 whitespace-pre-line" data-testid="streaming-upload-modal-status-message">
                {job.statusMessage || 'Upload could not be completed.'}
              </div>
              {job.errorMessage && job.errorMessage !== job.statusMessage && (
                <div className="mt-2 text-xs text-rose-700/80 whitespace-pre-line" data-testid="streaming-upload-modal-error-message">
                  Details: {job.errorMessage}
                </div>
              )}
              {/* iter1396 - Structured failure banner. When the backend
                  attached a taxonomy (failure.code + suggested_action),
                  show a specific "what to do next" card BEFORE the
                  generic layout-reject callout below. */}
              {failure && suggestion === 'resave_as_xlsx' && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={`upload-failure-${failure.code}`}>
                  <div className="text-xs font-semibold text-amber-900 mb-1">Try this fix</div>
                  <div className="text-xs text-amber-900 leading-relaxed">
                    Open the file in <strong>Excel</strong> or <strong>Google Sheets</strong>,
                    choose <strong>File &rarr; Save As &rarr; Excel Workbook (.xlsx)</strong>,
                    then upload the new file. .xls (Excel 97-2003) is the legacy binary format
                    that some export tools still use.
                  </div>
                </div>
              )}
              {failure && suggestion === 'unlock_and_reupload' && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={`upload-failure-${failure.code}`}>
                  <div className="text-xs font-semibold text-amber-900 mb-1">Try this fix</div>
                  <div className="text-xs text-amber-900 leading-relaxed">
                    Remove the password on the file (Excel: <em>File &rarr; Info &rarr; Protect &rarr; Encrypt</em>;
                    PDF: use your PDF viewer&apos;s security settings), then upload again.
                  </div>
                </div>
              )}
              {failure && suggestion === 'check_headers' && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={`upload-failure-${failure.code}`}>
                  <div className="text-xs font-semibold text-amber-900 mb-1">Try this fix</div>
                  <div className="text-xs text-amber-900 leading-relaxed">
                    We opened the file but couldn&apos;t find a header row with columns like
                    <em> Date</em>, <em>Debit</em>, <em>Credit</em>. Make sure the file has column headers
                    in one of the first 30 rows, or upload the PDF version instead.
                  </div>
                </div>
              )}
              {failure && suggestion === 'reexport_utf8' && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={`upload-failure-${failure.code}`}>
                  <div className="text-xs font-semibold text-amber-900 mb-1">Try this fix</div>
                  <div className="text-xs text-amber-900 leading-relaxed">
                    We couldn&apos;t decode the text in this file. Re-export as <strong>UTF-8</strong>
                    (in Excel: <em>Save As &rarr; CSV UTF-8</em>) or upload the statement as PDF.
                  </div>
                </div>
              )}
              {failure && suggestion === 'redownload_and_retry' && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={`upload-failure-${failure.code}`}>
                  <div className="text-xs font-semibold text-amber-900 mb-1">Try this fix</div>
                  <div className="text-xs text-amber-900 leading-relaxed">
                    The file appears damaged. Re-download it from your bank&apos;s portal and try again.
                  </div>
                </div>
              )}
              {failure && suggestion === 'use_supported_format' && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid={`upload-failure-${failure.code}`}>
                  <div className="text-xs font-semibold text-amber-900 mb-1">Supported formats</div>
                  <div className="text-xs text-amber-900 leading-relaxed">
                    We accept <strong>PDF, CSV, Excel (.xlsx/.xls), OFX and QIF</strong>.
                    The file you uploaded didn&apos;t match any of these formats.
                  </div>
                </div>
              )}
              {/* iter1245 · When the rejection is a parse/layout issue,
                  surface an explicit "upload CSV or Excel" callout so
                  users stop retrying the same PDF and instead grab the
                  spreadsheet export from their bank portal. Retry is
                  only useful for POSTING failures (network/mongo) —
                  layout rejections need a different FILE, not a retry. */}
              {isLayoutReject && (
                <div
                  className="mt-3 rounded-md border border-emerald-300 bg-emerald-50 p-3"
                  data-testid="streaming-upload-modal-csv-callout"
                >
                  <div className="text-xs font-semibold text-emerald-900 mb-1">
                    What to do
                  </div>
                  <div className="text-xs text-emerald-900 leading-relaxed">
                    Log into your bank&apos;s online portal, export the same
                    statement as <strong>CSV</strong> or <strong>Excel (.xlsx)</strong>,
                    and upload that file here. CSV / Excel parses cleanly
                    every time — no PDF layout guesswork. Most banks have
                    an &ldquo;Export&rdquo; or &ldquo;Download&rdquo; button next to your
                    transaction list.
                  </div>
                </div>
              )}
              {/* Only offer "Retry" if the failure isn't a layout reject
                  or a terminal-blocked continuity/reject — retrying the
                  same unparseable PDF or the same blocked upload just
                  wastes time. */}
              {!isLayoutReject && !isTerminalBlocked && (
                <div className="mt-3 text-xs text-slate-600">
                  We keep the transactions we already parsed. Clicking <strong>Retry</strong>
                  {' '}resumes posting from where we stopped — no re-parse, no extra charge.
                </div>
              )}
              {isTerminalBlocked && (
                <div
                  className="mt-3 text-xs text-slate-700 rounded-md bg-slate-50 border border-slate-200 p-3"
                  data-testid="streaming-upload-modal-blocked-explainer"
                >
                  This upload can&apos;t be resumed from where it stopped — the block
                  above is deterministic. If the numbers in the message look wrong,
                  correct the source file and upload it as a <em>new</em> upload.
                </div>
              )}
            </div>
          )}

          {job.batch && <StatementSetProgress batch={job.batch} />}
          {isActive && (
            <>
              {/* iter1238 Phase 8.13b · Legacy-friendly live status
                  banner. Works for BOTH pipelines — reads from the
                  job dict that the parent polls every 2 s. Was
                  previously invisible for legacy Excel/CSV uploads
                  because only StreamingParsedFeed rendered anything
                  and it required chord-state that legacy doesn't
                  produce. */}
              <div
                className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 mb-3"
                data-testid="streaming-upload-modal-live-status"
              >
                <div className="text-sm font-medium text-indigo-900 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                  <span data-testid="streaming-upload-modal-live-message">
                    {job.statusMessage || 'Working…'}
                  </span>
                </div>
                {typeof job.progress === 'number' && job.progress > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-indigo-800 mb-1">
                      <span>Progress</span>
                      <span data-testid="streaming-upload-modal-progress-pct">
                        {Math.min(100, Math.round(job.progress))}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-indigo-100 overflow-hidden">
                      <div
                        className="h-full bg-indigo-600 transition-all duration-500"
                        style={{ width: `${Math.min(100, Math.round(job.progress))}%` }}
                        data-testid="streaming-upload-modal-progress-bar"
                      />
                    </div>
                  </div>
                )}
                <div className="mt-3 text-xs text-indigo-700/80">
                  Large files take a few minutes. You can close this — we&apos;ll notify you when it&apos;s done.
                </div>
              </div>

              {/* Streaming-only: parsed-rows live feed */}
              <StreamingParsedFeed
                jobId={job.jobId}
                bankAccountId={null}
                onComplete={() => { /* handled by parent polling */ }}
              />
            </>
          )}

          {!isActive && !isFailed && (
            <div className="text-sm text-slate-600" data-testid="streaming-upload-modal-status-generic">
              Status: {job.status}
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          {isFailed && !isLayoutReject && !isTerminalBlocked && (
            <Button
              onClick={handleRetry}
              disabled={retrying}
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
              data-testid="streaming-upload-modal-retry-btn"
            >
              {retrying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Retrying…
                </>
              ) : (
                <>
                  <RefreshCcw className="w-4 h-4" />
                  Retry
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="gap-2"
            data-testid="streaming-upload-modal-close-btn"
          >
            <X className="w-4 h-4" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StreamingUploadModal;
